// ── Core Data Types ──

export interface BillingCategory {
  _id: string
  company: string
  division: string
  name: string
  description: string
  sort_order: number
  standardized?: boolean
  deleted: boolean
  created_at: number
  updated_at: number
}

export interface RateCardEntry {
  billing_category: string
  category: string
  rate: number
}

export interface BillingTargets {
  yearly_hours: number
  weekly_hours: number
  daily_hours: number
}

export interface RateCard {
  _id: string
  company: string
  division: string
  name: string
  default_rate: number
  entries: RateCardEntry[]
  billing_targets: BillingTargets
  deleted: boolean
  created_at: number
  updated_at: number
}

export interface TimeEntryTemplate {
  _id: string
  company: string
  division: string
  billing_category: string
  name: string
  category: string
  description: string
  billable: boolean
  default_duration?: number
  deleted: boolean
  created_at: number
  updated_at: number
}

export type TimeEntryStatus = "draft" | "approved" | "invoiced"
export type TimeEntrySource = "manual" | "timer" | "auto_activity" | "auto_create"
export type RateSource =
  | "matter_category"
  | "matter_user"
  | "matter_flat"
  | "rate_card"
  | "rate_card_default"
  | "user_rate"
  | "division_default"

export interface TimeEntryHistoryChange {
  field: string
  from: unknown
  to: unknown
}

export interface TimeEntryHistory {
  action: string
  user: string
  timestamp: number
  changes?: TimeEntryHistoryChange[]
}

export interface SourceActivity {
  source_collection: string
  document_id: string
  role?: string
  description?: string
  duration_minutes?: number
}

export interface TimeEntry {
  _id: string
  company: string
  division: string
  matter: string
  user: string
  created_by: string
  workflow?: string
  event?: string
  time_entry_template?: string
  billing_category: string
  invoice?: string

  description: string
  category: string
  activity: string
  date: string // YYYY-MM-DD

  duration_minutes: number
  billed_minutes: number
  start_time?: number
  end_time?: number

  billable: boolean
  rate: number
  rate_source: RateSource
  amount: number

  source: TimeEntrySource
  source_activities: SourceActivity[]

  status: TimeEntryStatus
  approved_by?: string
  approved_at?: number
  invoiced: boolean

  history: TimeEntryHistory[]

  deleted: boolean
  created_at: number
  updated_at: number
}

export interface ActiveTimer {
  _id: string
  company: string
  division: string
  user: string
  matter: string
  description: string
  category: string
  template: string
  started_at: number
  updated_at: number
}

// ── Request Types ──

export interface CreateTimeEntryRequest {
  matter: string
  time_entry_template?: string
  billing_category?: string
  description?: string
  date: string
  duration_minutes: number
  billable?: boolean
  user?: string
  event?: string
  start_time?: number
  end_time?: number
  source?: TimeEntrySource
}

export interface UpdateTimeEntryRequest {
  description?: string
  date?: string
  duration_minutes?: number
  billable?: boolean
  billing_category?: string
  time_entry_template?: string
  user?: string
  event?: string
  start_time?: number
  end_time?: number
}

export interface TimeEntrySearchParams {
  division?: string
  matter?: string
  user?: string
  billing_category?: string
  status?: TimeEntryStatus
  start_date?: string
  end_date?: string
  search?: string
  page?: number
  limit?: number
}

export interface ApproveEntriesRequest {
  time_entry_ids: string[]
}

export interface ImportToInvoiceRequest {
  time_entry_ids: string[]
  invoice_id?: string
  matter: string
}

export interface BatchInvoiceRequest {
  division: string
  date_range: { start: string; end: string }
  invoice_name_prefix: string
  user?: string
}

export interface TimerStartRequest {
  matter: string
  description?: string
  category?: string
  template?: string
}

export interface PreviewRateRequest {
  matter: string
  user?: string
  billing_category?: string
  category?: string
}

// ── Response Types ──

export interface PreviewRateResponse {
  rate: number
  source: RateSource
  rate_card_name?: string
}

export interface TimesheetMatterRow {
  matter_id: string
  matter_name: string
  days: Record<string, TimesheetDayCell>
  week_total_hours: number
  week_total_amount: number
}

export interface TimesheetDayCell {
  total_hours: number
  entries: TimeEntry[]
}

export interface TimesheetResponse {
  week_start: string
  matters: TimesheetMatterRow[]
  daily_totals: Record<string, number>
  week_total_hours: number
  week_total_billable_hours: number
  week_total_amount: number
}

export interface MySummaryPeriod {
  total_hours: number
  billable_hours: number
  amount: number
  entry_count: number
}

export interface MySummaryResponse {
  today: MySummaryPeriod
  this_week: MySummaryPeriod
  this_month: MySummaryPeriod
  this_year: MySummaryPeriod
  targets: BillingTargets & { source: string }
}

export interface BudgetSummary {
  budget: number
  total_consumed: number
  invoiced: number
  approved_unbilled: number
  draft: number
  remaining: number
  percentage_consumed: number
  alert: boolean
  alert_percentage: number
}

// ── Display Helpers ──

export const STATUS_LABELS: Record<TimeEntryStatus, string> = {
  draft: "Draft",
  approved: "Approved",
  invoiced: "Invoiced",
}

export const SOURCE_LABELS: Record<TimeEntrySource, string> = {
  manual: "Manual",
  timer: "Timer",
  auto_activity: "Auto (Activity)",
  auto_create: "Auto",
}

